import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Spin } from 'antd';
import { map } from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { FloorPlan } from '../../__generated__/graphql';
import { LIMIT, ROUTES } from '../../common/constants';
import FloorPlanCard from './components/FloorPlanCard';
import { GET_ALL_FLOOR_PLANS } from './graphql/queries';

//Import css
import './styles/allFloorPlans.less';

//Import Images
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { useWindowSize } from 'react-use';
import { AppContext } from '../../AppContext';
import PlaceHolderImage from '../../assets/images/dashboard-placeholder-image.png';
import { isMobileBrowser } from '../../common/utils';
import AddFloorPlanButton from '../../components/AddFloorPlanButton';
import useRouter from '../../hooks/useRouter';
import { AppActionType, AppContextType } from '../../types/appContext.type';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import { DELETE_FLOOR_PLAN } from './graphql/mutations';

export default function AllFloorPlans({
  onScrollChange,
}: {
  //onScrollChange prop is used to notify the parent about scroll state changes , so need this variable.
  // eslint-disable-next-line no-unused-vars
  onScrollChange: (scrolled: boolean) => void;
}) {
  const { dispatch } = useContext(AppContext) as AppContextType;
  const [floorPlans, setFloorPlans] = useState<FloorPlan[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [floorplansLoading, setFloorPlansLoading] = useState<boolean>(true);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { width } = useWindowSize();
  const { navigate } = useRouter();

  useEffect(() => {
    const handleScroll = () => {
      const element = containerRef.current;

      if (element) {
        const hasScrolled = element.scrollTop > 10;
        onScrollChange(hasScrolled);
      }
    };

    const element = containerRef.current;
    if (element) {
      element.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (element) {
        element.removeEventListener('scroll', handleScroll);
      }
    };
  }, [onScrollChange]);

  const [fetchPlans, { refetch }] = useLazyQuery(GET_ALL_FLOOR_PLANS, {
    variables: {
      data: { skip: 0, limit: LIMIT },
    },
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      const newFloorPlans = (res?.getFloorPlansWeb?.data as FloorPlan[]) || [];
      const totalCount = res?.getFloorPlansWeb?.total || 0;

      setFloorPlans([...floorPlans, ...newFloorPlans]);

      if (newFloorPlans.length < 10) {
        setHasMore(false);
      }

      const tourGuideStatus = totalCount === 0;
      if (dispatch) {
        dispatch({ type: AppActionType.setTourGuide, data: tourGuideStatus });
      }

      setFloorPlansLoading(false);
    },
    onError: () => {
      setFloorPlansLoading(false);
    },
  });
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<FloorPlan | null>();
  const [deleteFloorPlan, { loading: deleteLoading }] =
    useMutation(DELETE_FLOOR_PLAN);
  const handleDeleteFloorPlan = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    deleteFloorPlan({
      variables: {
        floorPlanId: selectedPlan?.id as string,
      },
      onCompleted: () => {
        setFloorPlans(
          floorPlans?.filter((plan) => plan?.id !== selectedPlan?.id),
        );
        setDeleteConfirmation(false);
      },
      onError: () => {
        setDeleteConfirmation(false);
      },
    });
  };

  useEffect(() => {
    fetchPlans();
  }, [refetch]);

  const handleScroll = async (event: React.UIEvent<HTMLElement>) => {
    const { target } = event;
    const scrollableDiv = target as HTMLElement;
    if (scrollableDiv) {
      const { scrollTop, scrollHeight, offsetHeight } = scrollableDiv;

      const scrolledToBottom = scrollTop + offsetHeight >= scrollHeight - 5;
      if (scrolledToBottom && hasMore) {
        setFloorPlansLoading(true);
        await fetchPlans({
          variables: {
            data: {
              skip: floorPlans?.length,
              limit: LIMIT,
            },
          },
        });
      }
    }
  };
  return (
    <>
      {floorplansLoading && floorPlans?.length === 0 && (
        <Spin indicator={<LoadingOutlined spin />} className="loader" />
      )}
      {!floorplansLoading && floorPlans?.length === 0 && (
        <div>
          <div className="placeholder-div">
            <img
              src={PlaceHolderImage}
              alt="Placeholder"
              className="placeholder-image"
            />
            <p className="body-base placeholder-text">
              Upload your first floor plan and quickly get a Vastu score for
              your property.
            </p>
          </div>
          <AddFloorPlanButton />
        </div>
      )}
      <div className="main-cards-div">
        <div
          ref={containerRef}
          className="cards-container"
          onScroll={handleScroll}
        >
          {floorPlans && floorPlans.length > 0 && (
            <div
              className="add-floor-plan-card"
              style={{
                display: !isMobileBrowser(width) ? 'flex' : 'none',
              }}
              onClick={() => {
                navigate(ROUTES.UPLOAD_FLOOR_PLAN);
                dispatch({ type: AppActionType.removeFloorPlanSequence });
              }}
            >
              <div className="div-content">
                <Button
                  className="add-new-floor-pan-btn"
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    navigate(ROUTES.UPLOAD_FLOOR_PLAN);
                    dispatch({ type: AppActionType.removeFloorPlanSequence });
                  }}
                ></Button>
                <div className="body-lg medium add-new-plan-card-text">
                  Add New Floor Plan
                </div>
              </div>
            </div>
          )}
          {map(floorPlans, (plans) =>
            plans ? (
              <FloorPlanCard
                key={plans.id ?? ''}
                plan={plans}
                setDeleteConfirmation={setDeleteConfirmation}
                setSelectedPlan={setSelectedPlan}
              />
            ) : null,
          )}
        </div>
      </div>
      {setDeleteConfirmation && (
        <DeleteConfirmModal
          open={deleteConfirmation}
          onCancel={() => {
            setSelectedPlan(null);
            setDeleteConfirmation(false);
          }}
          deleteLoading={deleteLoading}
          onConfirm={handleDeleteFloorPlan}
        />
      )}
    </>
  );
}
